import type { EducateStoryline } from '@/types/educate';

/**
 * Whether a value looks like a renderer uiConfig (layout/appearance tree) rather
 * than the small top-level flags object (e.g. `{ showKnolskapeLogo: true }`) that
 * Kreator also stores under `uiConfig`. The renderer config always carries at
 * least one of these structural keys.
 */
const isRendererUiConfig = (value: unknown): boolean =>
  !!value &&
  typeof value === 'object' &&
  ('layout' in value ||
    'appearance' in value ||
    'productLine' in value ||
    'schemaVersion' in value);

/**
 * The generic uiConfig tree the shared renderer needs. Kreator stores the real
 * renderer config under `storylineContext.uiConfig` and a separate flags object
 * (showKnolskapeLogo, …) at the top-level `uiConfig`, so we must pick the one
 * that actually is a renderer config — preferring the storylineContext copy —
 * and never hand the flags object to the renderer.
 */
export const resolveUiConfig = (storyline: EducateStoryline): unknown => {
  const candidates = [storyline.storylineContext?.uiConfig, storyline.uiConfig];
  return candidates.find(isRendererUiConfig) ?? null;
};

/**
 * Maps an engine unit id (e.g. `section-2`, `frame-0`, `slide-1`) to this
 * module's 4e unit id by position. Ids without a trailing index (e.g.
 * `node-<id>` for decision trees) pass straight through.
 */
export const positionalMapper =
  (unitIds: string[]) =>
  (engineUnitId: string): string => {
    const match = engineUnitId.match(/-(\d+)$/);
    if (!match) return engineUnitId;
    const index = Number(match[1]);
    return unitIds[index] ?? engineUnitId;
  };

export const identityMapper = (engineUnitId: string): string => engineUnitId;
